import React from 'react';
import ErrorState from 'components/ErrorState';
import { Submission } from 'types/evaluation';

interface EvaluationStatus {
  status: string;
  label: string;
  color: string;
}

interface SubmissionsListProps {
  filteredSubmissions: Submission[];
  error?: string | null;
  validUserCategories?: string[];
  onRetry?: () => void;
  onSelectSubmission: (submission: Submission) => void;
  getEvaluationStatus: (submission: Submission) => EvaluationStatus;
}

// Submissions List
const SubmissionsList: React.FC<SubmissionsListProps> = ({
  filteredSubmissions,
  error,
  validUserCategories,
  onRetry,
  onSelectSubmission,
  getEvaluationStatus,
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-smdd-card border border-gray-300 overflow-hidden">
      <div className="divide-y divide-gray-200">
        {error ? (
          // Error / all-completed state replaces the list
          <div className="flex items-center justify-center min-h-[400px]">
            {error.includes('have been completed') ? (
              <div className="text-center max-w-md mx-auto p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl">🎉</span>
                </div>

                <h3 className="text-xl font-baijamjuree font-bold text-gray-900 mb-3">
                  Excellent Work!
                </h3>

                <p className="text-gray-700 mb-6 font-baijamjuree">
                  You have completed all qualification evaluations for the{' '}
                  <strong>{(validUserCategories || []).join(', ')}</strong>{' '}
                  {(validUserCategories || []).length > 1
                    ? 'categories'
                    : 'category'}
                  .
                </p>
              </div>
            ) : (
              <div className="p-8 w-full">
                <ErrorState
                  title="No Submissions Available"
                  error={error}
                  onRetry={onRetry}
                />
              </div>
            )}
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-baijamjuree font-bold text-gray-900 mb-2">
              No submissions found
            </h3>
            <p className="text-gray-600 font-baijamjuree">
              No submissions match the current filter criteria.
            </p>
          </div>
        ) : (
          filteredSubmissions.map((submission, index) => {
            const submissionId = String(submission.documentId || submission.id);
            const evaluationStatus = getEvaluationStatus(submission);

            return (
              <div
                key={submissionId}
                className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => onSelectSubmission(submission)}
              >
                <div className="flex items-center gap-4">
                  {/* Numbering */}
                  <div className="flex-shrink-0 w-8 text-center text-gray-400 font-baijamjuree font-bold text-lg">
                    {index + 1}.
                  </div>

                  {/* Arrow Icon - moved to left side */}
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-baijamjuree text-gray-900 truncate flex-1 min-w-0">
                        {(() => {
                          // Individual categories that show full name instead of project name
                          const individualCategories = [
                            'Young Digital Leader Award',
                            'Women in Tech Award',
                          ];

                          const isIndividualCategory =
                            individualCategories.includes(submission.category);

                          if (isIndividualCategory) {
                            // For individual categories, show the full name instead of description
                            return (
                              submission.full_name ||
                              submission.description ||
                              'No Name'
                            );
                          } else {
                            // For organization categories, show the project name from description field
                            return (
                              submission.description ||
                              submission.project_name ||
                              submission.title ||
                              'No Project Name'
                            );
                          }
                        })()}
                      </h3>

                      {/* Status Badge */}
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-baijamjuree flex-shrink-0 ${
                          evaluationStatus.color === 'gray'
                            ? 'bg-gray-100 text-gray-800'
                            : evaluationStatus.color === 'yellow'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {evaluationStatus.label}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-600 font-baijamjuree">
                      <div>
                        <span className="font-medium">Submitter:</span>{' '}
                        {submission.full_name}
                      </div>
                      <div>
                        <span className="font-medium">Pillar:</span>{' '}
                        {submission.pillar}
                      </div>
                      <div>
                        <span className="font-medium">Organization:</span>{' '}
                        {submission.org_unit || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SubmissionsList;
